import { URLPattern } from 'urlpattern-polyfill';
import { isPersistableCta } from './campaignContent';

/**
 * Pure logic backing the full-page campaign builder: URL pattern validation
 * (shared with the legacy live-chat form), datetime-local <-> epoch-seconds
 * conversion for `starts_at`/`ends_at`, and the minimum-viable check the
 * Publish button runs before a draft goes live.
 */

export const DEFAULT_TIME_ON_PAGE = 10;

/** `trigger_rules.visitor_type` — which visitors see the campaign. */
export const VISITOR_TYPES = {
  ALL: 'all',
  FIRST_TIME: 'first_time',
  RETURNING: 'returning',
};

/** `trigger_rules.frequency` — how often the campaign re-triggers. */
export const FREQUENCIES = {
  EVERY_VISIT: 'every_visit',
  ONCE: 'once',
};

/** `trigger_rules.device_type` — which devices the campaign shows on. */
export const DEVICE_TYPES = {
  ALL: 'all',
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
};

/**
 * `trigger_rules.availability` — when the campaign is allowed to fire. Values
 * must match `Campaign::TRIGGER_RULES_AVAILABILITY`; anything else is
 * rejected, because the widget evaluates them client-side with no fallback.
 */
export const AVAILABILITIES = {
  ALWAYS: 'always',
  AGENTS_ONLINE: 'agents_online',
  BUSINESS_HOURS: 'business_hours',
};

/**
 * Where the campaign fires. Not itself a stored value: `everywhere` is the
 * absence of `trigger_rules.url`, so the payload omits the key entirely
 * rather than sending an empty string the model would reject.
 */
export const WHERE = {
  EVERYWHERE: 'everywhere',
  SPECIFIC_URL: 'specific_url',
};

/**
 * Where the builder's Test action opens a live chat campaign. A campaign
 * pinned to a URL is best seen on that page; an everywhere campaign has no
 * page of its own, so it falls back to the bundled widget test harness.
 */
export const WIDGET_TEST_PAGE_URL = 'http://localhost:8080/widget-test.html';

export const testPageUrlFor = campaign =>
  campaign?.trigger_rules?.url ||
  campaign?.inbox?.website_url ||
  WIDGET_TEST_PAGE_URL;

export const urlPatternValidators = {
  shouldBeAValidURLPattern: value => {
    try {
      // eslint-disable-next-line no-new
      new URLPattern(value);
      return true;
    } catch {
      return false;
    }
  },
  shouldStartWithHTTP: value =>
    value ? value.startsWith('https://') || value.startsWith('http://') : false,
};

/**
 * `starts_at`/`ends_at` arrive as epoch seconds (null when unset) but must be
 * sent back as ISO8601 strings — Rails casts datetime params from strings,
 * not epoch ints. datetime-local inputs speak `YYYY-MM-DDTHH:mm` in the
 * browser's local timezone, so the round trip is epoch → local for display
 * and local → ISO for the payload.
 */
export const epochToDatetimeLocal = epochSeconds => {
  if (!epochSeconds) return '';

  const date = new Date(epochSeconds * 1000);
  const pad = value => String(value).padStart(2, '0');
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const datetimeLocalToIso = value =>
  value ? new Date(value).toISOString() : null;

/**
 * Shifts a `#rrggbb` color by `amount` per channel (negative darkens).
 * Used to derive the second stop of the theme gradient.
 */
export const shiftHexColor = (hex, amount) => {
  const channels = [1, 3, 5].map(offset => {
    const channel = parseInt(hex.slice(offset, offset + 2), 16) + amount;
    return Math.min(255, Math.max(0, channel)).toString(16).padStart(2, '0');
  });
  return `#${channels.join('')}`;
};

/** The branded header/launcher gradient a theme color produces. */
export const themeGradient = hex =>
  `linear-gradient(135deg, ${shiftHexColor(hex, 24)} 0%, ${hex} 55%, ${shiftHexColor(hex, -32)} 100%)`;

/** Values double as i18n suffixes under CAMPAIGN.BUILDER.PUBLISH_ERRORS. */
export const PUBLISH_ERRORS = {
  CONTENT: 'CONTENT',
  CTAS: 'CTAS',
  INBOX: 'INBOX',
  URL: 'URL',
  TIME_ON_PAGE: 'TIME_ON_PAGE',
};

const isBlank = value =>
  value === null || value === undefined || String(value).trim() === '';

/**
 * The minimum a live-chat campaign needs before it can go live: something to
 * show (headline or body) and a complete trigger (inbox, time on page, and a
 * URL only when the campaign is pinned to a specific page — an everywhere
 * campaign has none by definition).
 *
 * @returns {string[]} `PUBLISH_ERRORS` values, empty when publishable.
 */
export const validateForPublish = ({
  headline,
  body,
  inboxId,
  where,
  url,
  timeOnPage,
  ctas,
}) => {
  const errors = [];

  if (isBlank(headline) && isBlank(body)) errors.push(PUBLISH_ERRORS.CONTENT);
  // An incomplete CTA is dropped from the payload rather than rejected, so it
  // would keep showing in the preview while never reaching the widget. Publish
  // is the point where that silent gap has to surface.
  if ((ctas ?? []).some(cta => !isPersistableCta(cta)))
    errors.push(PUBLISH_ERRORS.CTAS);
  if (!inboxId) errors.push(PUBLISH_ERRORS.INBOX);
  if (where === WHERE.SPECIFIC_URL && isBlank(url))
    errors.push(PUBLISH_ERRORS.URL);
  if (isBlank(timeOnPage)) errors.push(PUBLISH_ERRORS.TIME_ON_PAGE);

  return errors;
};
